import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, ShoppingCart, FileText, Eye, Pencil } from "lucide-react";
import Pagination from "../../components/shared/Pagination";
import InfinityCaseDetail from "../../components/admin/InfinityCaseDetail";
import CaseFormDialog, { PRINT_LABEL } from "../../components/reseller/CaseFormDialog";
import { useModules } from "../../context/ModuleContext";
import ModuleReadOnlyNotice from "../../components/reseller/ModuleReadOnlyNotice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 10;

export default function SavedPrintOrders() {
  const navigate = useNavigate();
  // Infinity Touch off: saved prints stay listed and viewable, but creating,
  // editing and ordering from them is gone — which also makes the embedded
  // CaseFormDialog unreachable.
  const { canWrite } = useModules();
  const readOnly = !canWrite("infinity_touch");
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  // The record being edited; null while creating a new one.
  const [editRecord, setEditRecord] = useState(null);
  // Read-only case-detail popup (same as admin) for a saved record.
  const [caseModalId, setCaseModalId] = useState(null);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/memorial-records`);
      setRecords(res.data?.data || res.data || []);
    } catch (e) {
      toast.error("Failed to load saved print orders");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchRecords(); }, []);

  const openCreate = () => { setEditRecord(null); setOpen(true); };
  const openEdit = (record) => { setEditRecord(record); setOpen(true); };

  const subjectName = (s) => [s?.first_name, s?.last_name].filter(Boolean).join(" ") || "—";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="saved-print-orders">
      <ModuleReadOnlyNotice module="infinity_touch" />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-6 h-6" /> Saved Print
          </h2>
          <p className="text-slate-500">Memorial print records captured for your families. Proceed from one to place a product order.</p>
        </div>
        {!readOnly && (
        <Button onClick={openCreate} className="rounded-xl">
          <Plus className="w-4 h-4 mr-2" /> New Saved Print
        </Button>
        )}
      </div>

      <div className="card-elevated overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">Case #</TableHead>
              <TableHead className="font-semibold">Deceased / Pet</TableHead>
              <TableHead className="font-semibold">Print Type</TableHead>
              <TableHead className="font-semibold">Captures</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">Loading…</TableCell></TableRow>
            ) : records.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">No saved print orders yet.</TableCell></TableRow>
            ) : records.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((r) => (
              <TableRow key={r.record_id} className="table-row cursor-pointer" onClick={() => navigate(`/reseller/print-records/${r.record_id}`)}>
                <TableCell className="font-medium">{r.case_number}</TableCell>
                <TableCell>{subjectName(r.subject)}</TableCell>
                <TableCell>{PRINT_LABEL[r.print_type] || r.print_type}</TableCell>
                <TableCell>{(r.captures || []).length}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={r.status === "ordered" ? "border-green-200 bg-green-50 text-green-700" : "border-slate-200 bg-slate-50 text-slate-600"}>
                    {r.status === "ordered" ? "Ordered" : "Saved"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-2">
                    <Button variant="ghost" size="sm" className="text-slate-600" onClick={(e) => { e.stopPropagation(); setCaseModalId(r.record_id); }}>
                      <Eye className="w-4 h-4 mr-1" /> View details
                    </Button>
                    {!readOnly && (<>
                    <Button variant="ghost" size="sm" className="text-slate-600" onClick={(e) => { e.stopPropagation(); openEdit(r); }}>
                      <Pencil className="w-4 h-4 mr-1" /> Edit details
                    </Button>
                    <Button size="sm" className="rounded-lg" onClick={(e) => { e.stopPropagation(); navigate(`/reseller/print-records/${r.record_id}/order`); }}>
                      <ShoppingCart className="w-4 h-4 mr-1" /> Create Order
                    </Button>
                    </>)}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Pagination
        page={page}
        totalPages={Math.max(1, Math.ceil(records.length / PAGE_SIZE))}
        onChange={setPage}
        totalItems={records.length}
        pageSize={PAGE_SIZE}
      />

      {/* Same case-centric popup the admin sees, read-only (no status change). */}
      <InfinityCaseDetail
        open={!!caseModalId}
        onClose={() => setCaseModalId(null)}
        memorialRecordId={caseModalId}
        apiBase="reseller"
        readOnly
      />

      <CaseFormDialog
        open={open}
        onOpenChange={setOpen}
        record={editRecord}
        onSaved={fetchRecords}
      />
    </div>
  );
}
